import { API } from "@/lib/api";
import { copy } from "./SecretField";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";

const BASE = API;

const ENDPOINTS = [
  ["GET", "/health", "Service + stream + market status (no auth)"],
  ["GET", "/auth/status", "Kite session state, token countdown, auto-login state"],
  ["GET", "/market/status?exchange=NSE", "Open/closed, holiday, next open"],
  ["GET", "/instruments/search?q=RELIANCE&exchange=NSE&limit=25", "Search full instrument master"],
  ["GET", "/symbols/{id}/snapshot", "Latest snapshot (id = token or NSE:RELIANCE); auto-subscribes"],
  ["GET", "/snapshots?ids=NSE:INFY,256265", "Batch snapshots (no auto-subscribe)"],
  ["POST", "/symbols/{id}/subscribe", "Force a live subscription"],
  ["POST", "/symbols/{id}/unsubscribe", "Release an on-demand subscription"],
  ["GET", "/symbols/{id}/depth", "Top-5 bid/ask (on demand)"],
  ["GET", "/symbols/{id}/candles?interval=15m&from=&to=", "1m,5m,15m,1h,1d,1w candles"],
  ["GET", "/watchlist/live", "Curated watchlist snapshots"],
  ["GET", "/indices/live?family=NIFTY", "Curated index snapshots"],
  ["GET", "/gainers-losers?n=10", "Top movers over configured universe"],
  ["GET", "/option-chain?underlying=NIFTY&expiry=&strikes=10&greeks=true", "Option chain with IV/Greeks/OI change"],
  ["GET", "/option-chain/underlyings | /expiries?underlying=NIFTY", "Option chain pickers"],
  ["GET", "/subscriptions", "Active subscriptions, caps, TTL"],
  ["GET", "/alerts?limit=30", "Recent service events"],
];

const PY = `import requests

BASE = "${BASE}"
API_KEY = "paste-your-master-key"      # Settings -> Master API key
H = {"X-API-Key": API_KEY}

class KiteBridge:
    def __init__(self, base=BASE, key=API_KEY):
        self.base, self.h = base, {"X-API-Key": key}
    def _get(self, path, **params):
        r = requests.get(self.base + path, headers=self.h, params=params, timeout=15)
        r.raise_for_status()
        return r.json()
    def health(self):            return requests.get(self.base + "/health", timeout=10).json()
    def search(self, q, **kw):   return self._get("/instruments/search", q=q, **kw)["results"]
    def snapshot(self, sid):     return self._get(f"/symbols/{sid}/snapshot")
    def snapshots(self, ids):    return self._get("/snapshots", ids=",".join(ids))["snapshots"]
    def depth(self, sid):        return self._get(f"/symbols/{sid}/depth")
    def candles(self, sid, interval="15m", **kw): return self._get(f"/symbols/{sid}/candles", interval=interval, **kw)
    def indices(self):           return self._get("/indices/live")["snapshots"]
    def watchlist(self):         return self._get("/watchlist/live")["snapshots"]
    def gainers_losers(self, n=10): return self._get("/gainers-losers", n=n)
    def option_chain(self, underlying, expiry=None, strikes=10, greeks=True):
        return self._get("/option-chain", underlying=underlying, expiry=expiry, strikes=strikes, greeks=str(greeks).lower())

if __name__ == "__main__":
    kb = KiteBridge()
    print(kb.health()["status"])
    print(kb.snapshot("NSE:RELIANCE")["ltp"])
    for g in kb.gainers_losers(5)["gainers"]:
        print(g["tradingsymbol"], g["change_pct"])`;

const Block = ({ title, text, testid }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-wider text-slate-500">{title}</span>
      <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" data-testid={`${testid}-copy`} onClick={() => copy(text, "Copied")}>
        <Copy className="mr-1 h-3 w-3" /> Copy
      </Button>
    </div>
    <pre data-testid={testid} className="scroll-thin max-h-72 w-full max-w-full overflow-auto whitespace-pre-wrap break-all rounded border border-slate-800 bg-slate-950 p-3 font-mono text-[11px] leading-relaxed text-slate-300">{text}</pre>
  </div>
);

export const ApiGuideTab = () => (
  <div className="min-w-0 space-y-4 pt-3" data-testid="apiguide-tab">
    <div className="rounded border border-slate-800 bg-slate-950/40 p-3 text-xs text-slate-400">
      <b className="text-slate-200">Connecting Server B.</b> It only needs this base URL and the master API key — nothing else.
      Send the key in the <span className="font-mono text-cyan-300">X-API-Key</span> header on every request. Everything is JSON over HTTPS;
      all clients share the same in-memory snapshots, so polling the same symbol from many machines is cheap.
    </div>
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">Base URL</div>
      <div className="flex items-center gap-2 rounded border border-slate-800 bg-slate-950/60 px-3 py-2">
        <span data-testid="apiguide-base-url" className="flex-1 break-all font-mono text-sm text-slate-200">{BASE}</span>
        <button data-testid="apiguide-base-copy" onClick={() => copy(BASE, "Base URL copied")} className="text-slate-500 hover:text-slate-200"><Copy className="h-4 w-4" /></button>
      </div>
    </div>
    <Block title="One-line test (curl)" testid="apiguide-curl"
      text={`curl -H "X-API-Key: <master-key>" "${BASE}/symbols/NSE:RELIANCE/snapshot"`} />
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">Endpoints</div>
      <div className="scroll-thin max-h-64 overflow-auto rounded border border-slate-800">
        <table className="w-full font-mono text-[11px]">
          <tbody>
            {ENDPOINTS.map(([m, p, d]) => (
              <tr key={p} className="border-b border-slate-800/60">
                <td className={`px-2 py-1.5 font-semibold ${m === "GET" ? "text-emerald-400" : "text-amber-400"}`}>{m}</td>
                <td className="px-2 py-1.5 text-slate-200">{p}</td>
                <td className="px-2 py-1.5 text-slate-500">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    <Block title="Ready-to-use Python client for Server B" testid="apiguide-python" text={PY} />
    <p className="text-[11px] text-slate-500">
      Errors return <span className="font-mono">{`{ "error": code, "message": text }`}</span> with a matching HTTP status (401 bad key,
      404 unknown symbol, 429 rate-limited / cap reached, 503 auth_required). On-demand calls (depth, candles, subscribe, option chain) are
      rate-limited per client; rotate the key anytime from the Master API key tab.
    </p>
  </div>
);
